import { FormInput, FormLabel, Form, Text, FormFieldGroup, Button, Alert } from '$components'
import { ChevronLeftIcon } from '$components'
import { ElementIntent } from '$components'
import * as z from 'zod'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { AuthRoutePath } from '../routes/public'
import { authForgotPasswordMutation } from '$api-client/@tanstack/react-query.gen'
import { QueryErrorHelper } from '$utils/query'
import { usePageTitle } from '$components'

enum Input {
  Email = 'email',
}

const schema = z.object({
  [Input.Email]: z.string().min(1, 'Email is required').email().trim(),
})

type FormValues = z.infer<typeof schema>

export function ForgotPasswordPage() {
  usePageTitle('Forgot Password')
  const navigate = useNavigate()

  const { mutate: sendResetEmail, isPending, error } = useMutation({
    ...authForgotPasswordMutation(),
    onSuccess: () => navigate(AuthRoutePath.ForgotPasswordConfirmation, { replace: true }),
  })

  const onSubmit = (values: FormValues) => {
    sendResetEmail({
      body: {
        email: values.email,
      },
    })
  }

  return (
    <div className="flex items-center justify-center min-h-screen w-full">
      <div className="w-full max-w-prose-sm flex flex-col gap-stack-section">
        <Link
          to={AuthRoutePath.Login}
          className="flex items-center gap-stack-dense text-body-sm hover:underline -ml-[5px] text-fg-subtle"
        >
          <ChevronLeftIcon className="size-4" />
          Back to login
        </Link>

        <div className="space-y-stack-tight">
          <Text type="h1">
            Forgot your password?
          </Text>

          <Text className="text-fg-subtle">
            Enter your email address and we&apos;ll send you a link to reset your password.
          </Text>
        </div>

        <Form
          onSubmit={onSubmit}
          schema={schema}
        >
          {() => (
            <div className="flex flex-col gap-stack-roomy">
              <FormFieldGroup>
                <FormLabel htmlFor={Input.Email}>
                  Email
                </FormLabel>

                <FormInput
                  name={Input.Email}
                  placeholder="Enter your email address"
                />
              </FormFieldGroup>

              <Button
                type="submit"
                disabled={isPending}
                isLoading={isPending}
                className="w-full mt-stack-roomy"
                size="lg"
              >
                Send reset link
              </Button>

              { error && (
                <Alert intent={ElementIntent.Danger}>
                  <Alert.Heading>
                    Something went wrong!
                  </Alert.Heading>

                  <Alert.Body>
                    { QueryErrorHelper.getQueryError(error).message }
                  </Alert.Body>
                </Alert>
              )}
            </div>
          )}
        </Form>
      </div>
    </div>
  )
}
